import { z } from 'zod';

import {
  civilDateInZone,
  civilDaysBetween,
  coerceCivilDate,
  DEFAULT_PLATFORM_TIMEZONE,
  isCivilDate,
  isRealCivilDay,
} from '../utils/date';

/**
 * A date-only wire value (D5): a bare civil 'yyyy-MM-dd' passes through,
 * while a legacy ISO instant — stale bundles serialize the picker's local
 * midnight via `toISOString()` — is normalized to the platform zone's civil
 * day, the same rule the column-migration backfills used. Output is always
 * a civil string, so services and DATE columns never see an instant.
 */
export const CivilDateDto = z
  .union([z.string(), z.date()])
  .refine(
    (value) =>
      value instanceof Date
        ? !Number.isNaN(value.getTime())
        : isCivilDate(value)
          ? isRealCivilDay(value)
          : !Number.isNaN(Date.parse(value)),
    { message: 'VALIDATION__InvalidDate' },
  )
  .transform((value) => coerceCivilDate(value, DEFAULT_PLATFORM_TIMEZONE));

/**
 * D1 (global-timezone spec): the scheduler-PICKED civil day rides next to
 * the instant it annotates (`businessDate` + `dateTime`). Coherence rules
 * shared by all four schedule-domain event wires:
 * - the day never travels alone — an update carrying a day without its
 *   instant has nothing to annotate;
 * - no real timezone shifts a civil day more than ONE step from the
 *   instant's UTC day, so a larger skew is always a client bug.
 */
export function assertPickedDayCoherent(
  ctx: z.RefinementCtx,
  businessDate: string | null | undefined,
  dateTime: Date | string | null | undefined,
) {
  if (businessDate == null) return;
  if (dateTime == null) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'VALIDATION__Required',
      path: ['dateTime'],
    });
    return;
  }
  const utcDay = civilDateInZone(new Date(dateTime), 'UTC');
  if (Math.abs(civilDaysBetween(utcDay, businessDate)) > 1) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'VALIDATION__InvalidRange',
      path: ['businessDate'],
    });
  }
}

/**
 * A bare wall-clock time-of-day, 'HH:mm' ONLY, no date/zone attached (the
 * zone is resolved separately per-user at read time, e.g. Daily Activity's
 * reminder times).
 */
export const CivilTimeDto = z
  .string()
  .regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'VALIDATION__InvalidTime');

/**
 * The scheduler-PICKED civil day on the wire — BARE 'yyyy-MM-dd' ONLY.
 * Deliberately NOT CivilDateDto: its legacy-instant normalization recovers
 * the PLATFORM-zone day, which is the wrong day for exactly the users this
 * field exists for. Nothing ever sent the field before it existed, so there
 * is no stale-bundle shape to tolerate.
 */
export const PickedCivilDayDto = z
  .string()
  .refine((value) => isCivilDate(value) && isRealCivilDay(value), {
    message: 'VALIDATION__InvalidDate',
  });
